import React, { useState, useEffect } from 'react';
import { X, Truck, DollarSign, CheckCircle, AlertCircle, FileText, Package } from 'lucide-react';
import { bilmottakService, ActiveBilmottakSessionDto, CloseBilmottakResponse, BilmottakOrderSummary } from '../../../../services/bilmottakService';

interface CloseBilmottakModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: ActiveBilmottakSessionDto;
  onClosed: () => void;
}

/**
 * Modal for closing a Bilmottak session.
 * Shows summary, per-order freight cost inputs, and triggers ERP export.
 */
export const CloseBilmottakModal: React.FC<CloseBilmottakModalProps> = ({
  isOpen,
  onClose,
  session,
  onClosed
}) => {
  const [freightCosts, setFreightCosts] = useState<Record<string, string>>({});
  const [freightDescription, setFreightDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CloseBilmottakResponse | null>(null);
  const [orders, setOrders] = useState<BilmottakOrderSummary[]>([]);

  // Fetch orders when modal opens
  useEffect(() => {
    if (isOpen && session.batchId) {
      setLoadingOrders(true);
      bilmottakService.getSessionOrders(session.batchId)
        .then((data) => {
          setOrders(data);
          // Initialize freight cost inputs with empty strings
          const initial: Record<string, string> = {};
          data.forEach(o => { initial[o.orderNoERP] = ''; });
          setFreightCosts(initial);
        })
        .catch((err) => {
          console.error('Error fetching session orders:', err);
        })
        .finally(() => setLoadingOrders(false));
    }
  }, [isOpen, session.batchId]);

  const handleClose = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build per-order freight cost map
      const freightCostPerOrder: Record<string, number> = {};
      for (const order of orders) {
        const raw = freightCosts[order.orderNoERP]?.trim();
        if (raw) {
          const cost = parseFloat(raw.replace(',', '.'));
          if (isNaN(cost) || cost < 0) {
            setError(`Ugyldig fraktkostnad for ${order.orderNoERP}. Angi et positivt tall.`);
            setLoading(false);
            return;
          }
          freightCostPerOrder[order.orderNoERP] = cost;
        } else {
          freightCostPerOrder[order.orderNoERP] = 0;
        }
      }

      const response = await bilmottakService.closeBilmottak(
        session.batchId,
        freightCostPerOrder,
        freightDescription.trim() || undefined
      );

      if (!response.success) {
        setError(response.errorMessage || 'Kunne ikke avslutte bilmottak');
        return;
      }

      setResult(response);
    } catch (err) {
      console.error('Error closing bilmottak:', err);
      setError('En feil oppstod ved avslutning av bilmottak');
    } finally {
      setLoading(false);
    }
  };

  const handleDone = () => {
    setResult(null);
    setFreightCosts({});
    setFreightDescription('');
    setError(null);
    setOrders([]);
    onClosed();
    onClose();
  };

  const updateFreightCost = (orderNoERP: string, value: string) => {
    setFreightCosts(prev => ({ ...prev, [orderNoERP]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-purple-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <Truck className="h-6 w-6" />
            <div>
              <h2 className="text-lg font-semibold">Avslutt bilmottak</h2>
              <p className="text-purple-100 text-sm">{session.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-purple-500 rounded-full p-1 transition-colors"
            disabled={loading}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          {/* Success result */}
          {result ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-green-800">Bilmottak avsluttet</p>
                  <p className="text-sm text-green-700 mt-1">
                    {result.exportedFiles} fil(er) eksportert med {result.exportedRecords} linje(r)
                  </p>
                </div>
              </div>
              <button
                onClick={handleDone}
                className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Lukk
              </button>
            </div>
          ) : (
            <>
              {/* Error */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
                  <AlertCircle className="h-5 w-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h3 className="text-sm font-medium text-gray-700">Oppsummering</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-gray-500">Sesjon:</div>
                  <div className="font-medium">{session.batchId}</div>
                  <div className="text-gray-500">Lager:</div>
                  <div className="font-medium">{session.warehouse || '-'}</div>
                  <div className="text-gray-500">Operatører:</div>
                  <div className="font-medium">{session.activeOperatorCount}</div>
                  <div className="text-gray-500">Linjer mottatt:</div>
                  <div className="font-medium">{session.totalLinesReceived}</div>
                  <div className="text-gray-500">Antall mottatt:</div>
                  <div className="font-medium">{session.totalQuantityReceived?.toFixed(1) || '0'}</div>
                  <div className="text-gray-500">Ordrer berørt:</div>
                  <div className="font-medium">{session.ordersAffected}</div>
                </div>
              </div>

              {/* Per-order freight cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    <span>Fraktkostnad pr. ordre (valgfritt)</span>
                  </div>
                </label>
                {loadingOrders ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full" />
                    <span>Henter ordrer...</span>
                  </div>
                ) : orders.length === 0 ? (
                  <p className="text-sm text-gray-400">Ingen ordrer funnet i sesjonen.</p>
                ) : (
                  <div className="space-y-2">
                    {orders.map((order) => (
                      <div key={order.orderNoERP} className="flex items-center gap-3 bg-gray-50 rounded-lg px-3 py-2">
                        <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">{order.orderNoERP}</div>
                          <div className="text-xs text-gray-500">{order.lineCount} {order.lineCount === 1 ? 'linje' : 'linjer'}, {order.totalQuantity.toFixed(1)} stk</div>
                        </div>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={freightCosts[order.orderNoERP] || ''}
                          onChange={(e) => updateFreightCost(order.orderNoERP, e.target.value)}
                          placeholder="0,00"
                          className="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent text-right"
                        />
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">
                  Sendes med i ERP-eksportfilen. Ikke lagret i databasen.
                </p>
              </div>

              {/* Freight description */}
              <div>
                <label htmlFor="freightDesc" className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span>Referanse / beskrivelse (valgfritt)</span>
                  </div>
                </label>
                <input
                  id="freightDesc"
                  type="text"
                  value={freightDescription}
                  onChange={(e) => setFreightDescription(e.target.value)}
                  placeholder="F.eks. fraktreferanse, bilnr..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Info */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                <p className="font-medium">Viktig</p>
                <p className="mt-1">
                  Avslutning eksporterer alle mottatte linjer til ERP og setter sesjonen som avsluttet.
                  Alle operatører må ha fullført sine linjer først.
                </p>
              </div>

              {/* Buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  Avbryt
                </button>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={loading || loadingOrders}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      <span>Avslutter...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      <span>Bekreft og eksporter til ERP</span>
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
